import React from 'react';
import PublicLayout from '../../components/layout/PublicLayout';
import SEO from '../../components/SEO';
import { FileText, Shield, Phone, Mail, MapPin } from 'lucide-react';

const TermsAndConditions = () => {
    return (
        <PublicLayout>
            <SEO
                title="Terms and Conditions"
                description="Terms and conditions for booking retiring rooms through Retiring Room. Understand our policies on booking, cancellation, check-in, and payment."
                canonical="/terms-and-conditions"
                keywords="retiring room terms, booking terms and conditions, railway retiring room policy, cancellation policy"
            />
            <div className="bg-gradient-to-b from-midnight-950 via-midnight-900 to-midnight-950 min-h-screen py-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Header */}
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-railway-500 to-railway-600 rounded-2xl mb-6 shadow-lg shadow-railway-500/30">
                            <FileText className="w-8 h-8 text-white" />
                        </div>
                        <h1 className="text-4xl font-display font-bold text-white mb-3">Terms and Conditions</h1>
                        <div className="flex flex-col items-center gap-1 text-white/60">
                            <p className="text-sm">Operated by: <strong className="text-white/80">JUNES KITCHEN AND BAKERS</strong></p>
                            <p className="text-sm">Last updated: 21-01-2026</p>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-8 shadow-xl">
                        <div className="prose prose-invert prose-sm max-w-none space-y-8">

                            {/* Section 1 */}
                            <section>
                                <h2 className="text-xl font-display font-semibold text-white border-b border-white/10 pb-3 mb-4">1. Acceptance of Terms</h2>
                                <p className="text-white/70 leading-relaxed">
                                    By accessing or using retiringroom.in ("Website"), operated by JUNES KITCHEN AND BAKERS, you agree to be bound by these Terms and Conditions, including the Refund & Cancellation Policy and Privacy Policy.
                                </p>
                                <p className="text-white/70 leading-relaxed">
                                    If you do not agree, please refrain from using the Website or its services.
                                </p>
                            </section>

                            {/* Section 2 */}
                            <section>
                                <h2 className="text-xl font-display font-semibold text-white border-b border-white/10 pb-3 mb-4">2. About the Service</h2>
                                <p className="text-white/70 leading-relaxed">
                                    retiringroom.in is an online booking platform operated by JUNES KITCHEN AND BAKERS that enables passengers to check availability and book railway retiring rooms at select railway stations.
                                </p>
                                <div className="mt-4 p-4 bg-railway-500/10 rounded-xl border border-railway-500/20">
                                    <p className="text-white/80 font-medium mb-2">Currently active locations:</p>
                                    <ul className="list-disc list-inside text-white/70 space-y-1">
                                        <li>Kannur Railway Station, Kerala</li>
                                        <li>Bhusaval Railway Station, Maharashtra</li>
                                    </ul>
                                </div>
                                <p className="text-white/70 leading-relaxed mt-4">
                                    The Website acts solely as a booking facilitation platform. Actual accommodation, room allocation, verification, and stay management are handled by the respective station's retiring room reception.
                                </p>
                            </section>

                            {/* Section 3 */}
                            <section>
                                <h2 className="text-xl font-display font-semibold text-white border-b border-white/10 pb-3 mb-4">3. User Eligibility & Responsibility</h2>
                                <ul className="list-disc list-inside text-white/70 space-y-2">
                                    <li>Users must be 18 years or older.</li>
                                    <li>Accurate personal details (name, mobile number, valid government-issued ID) must be provided.</li>
                                    <li>The same ID used during booking must be produced at the reception.</li>
                                    <li>Bookings are non-transferable.</li>
                                </ul>
                            </section>

                            {/* Section 4 */}
                            <section>
                                <h2 className="text-xl font-display font-semibold text-white border-b border-white/10 pb-3 mb-4">4. Booking Confirmation & Check-In</h2>
                                <ul className="list-disc list-inside text-white/70 space-y-2">
                                    <li>A booking is confirmed only after successful payment or selection of pay-at-station option.</li>
                                    <li>Booking confirmation details are displayed on-screen and/or sent via SMS.</li>
                                    <li>Guests must report to the retiring room reception within the selected check-in time.</li>
                                    <li>Failure to report may result in cancellation without refund, as per station rules.</li>
                                </ul>
                            </section>

                            {/* Section 5 */}
                            <section>
                                <h2 className="text-xl font-display font-semibold text-white border-b border-white/10 pb-3 mb-4">5. Room Allocation & Stay Rules</h2>
                                <ul className="list-disc list-inside text-white/70 space-y-2">
                                    <li>Room allocation is subject to availability at the time of check-in.</li>
                                    <li>Early check-in or late check-out is not guaranteed and may attract additional charges.</li>
                                    <li>Extension of stay is subject to availability and approval by reception staff.</li>
                                    <li>Management reserves the right to deny accommodation in case of rule violations or misconduct.</li>
                                </ul>
                            </section>

                            {/* Section 6 */}
                            <section>
                                <h2 className="text-xl font-display font-semibold text-white border-b border-white/10 pb-3 mb-4">6. Cancellation & Refund Policy</h2>
                                <p className="text-white/70 leading-relaxed mb-4">
                                    Refunds and cancellations are governed strictly by the following rules:
                                </p>
                                <div className="space-y-3">
                                    <div className="p-4 bg-amber-500/10 rounded-xl border border-amber-500/20">
                                        <p className="text-amber-400 font-medium">20% deduction</p>
                                        <p className="text-white/70 text-sm">of the booking amount if cancelled 2 days in advance, excluding the day of occupation.</p>
                                    </div>
                                    <div className="p-4 bg-orange-500/10 rounded-xl border border-orange-500/20">
                                        <p className="text-orange-400 font-medium">50% deduction</p>
                                        <p className="text-white/70 text-sm">of the booking amount if cancelled 1 day in advance, excluding the day of occupation.</p>
                                    </div>
                                    <div className="p-4 bg-red-500/10 rounded-xl border border-red-500/20">
                                        <p className="text-red-400 font-medium">No refund</p>
                                        <p className="text-white/70 text-sm">shall be admissible if the booking is cancelled on the same day of occupation.</p>
                                    </div>
                                </div>
                                <p className="text-white/60 text-sm mt-4 italic">
                                    These rules apply uniformly across all participating railway stations.
                                </p>
                            </section>

                            {/* Section 7 */}
                            <section>
                                <h2 className="text-xl font-display font-semibold text-white border-b border-white/10 pb-3 mb-4">7. Refund Processing</h2>
                                <ul className="list-disc list-inside text-white/70 space-y-2">
                                    <li>Eligible refunds will be processed to the original payment method.</li>
                                    <li>Refund processing time is typically 7–10 working days, depending on the bank or payment gateway.</li>
                                    <li>No cash refunds will be issued at the station for online payments.</li>
                                </ul>
                            </section>

                            {/* Section 8 */}
                            <section>
                                <h2 className="text-xl font-display font-semibold text-white border-b border-white/10 pb-3 mb-4">8. User Conduct</h2>
                                <p className="text-white/70 leading-relaxed mb-3">
                                    Users agree not to:
                                </p>
                                <ul className="list-disc list-inside text-white/70 space-y-2">
                                    <li>Misuse the Website or attempt unauthorized access.</li>
                                    <li>Provide false or misleading information.</li>
                                    <li>Disrupt station operations or violate railway rules.</li>
                                </ul>
                                <p className="text-white/60 text-sm mt-3 italic">
                                    Violation may result in cancellation without refund and possible legal action.
                                </p>
                            </section>

                            {/* Section 9 */}
                            <section>
                                <h2 className="text-xl font-display font-semibold text-white border-b border-white/10 pb-3 mb-4">9. Limitation of Liability</h2>
                                <p className="text-white/70 leading-relaxed mb-3">
                                    JUNES KITCHEN AND BAKERS, operating retiringroom.in, shall not be liable for:
                                </p>
                                <ul className="list-disc list-inside text-white/70 space-y-2">
                                    <li>Loss of personal belongings during the stay.</li>
                                    <li>Delays, cancellations, or service disruptions caused by railway operations, force majeure events, or technical issues.</li>
                                    <li>Any indirect, incidental, or consequential damages.</li>
                                </ul>
                                <p className="text-white/60 text-sm mt-3 italic">
                                    Accommodation services are provided on an "as-is" basis subject to station regulations.
                                </p>
                            </section>

                            {/* Section 10 */}
                            <section>
                                <h2 className="text-xl font-display font-semibold text-white border-b border-white/10 pb-3 mb-4">10. Indemnification</h2>
                                <p className="text-white/70 leading-relaxed">
                                    You agree to indemnify and hold harmless JUNES KITCHEN AND BAKERS, retiringroom.in, its operators, and associated station authorities from any claims, losses, damages, or liabilities arising from misuse of the platform or violation of these terms.
                                </p>
                            </section>

                            {/* Section 11 */}
                            <section>
                                <h2 className="text-xl font-display font-semibold text-white border-b border-white/10 pb-3 mb-4">11. Intellectual Property</h2>
                                <p className="text-white/70 leading-relaxed">
                                    All content, branding, logos, and materials on retiringroom.in are protected under applicable intellectual property laws and may not be reused without written permission from JUNES KITCHEN AND BAKERS.
                                </p>
                            </section>

                            {/* Section 12 */}
                            <section>
                                <h2 className="text-xl font-display font-semibold text-white border-b border-white/10 pb-3 mb-4">12. Privacy Policy</h2>
                                <p className="text-white/70 leading-relaxed">
                                    Use of the Website is governed by the Privacy Policy outlined below.
                                </p>
                            </section>

                            {/* Section 13 */}
                            <section>
                                <h2 className="text-xl font-display font-semibold text-white border-b border-white/10 pb-3 mb-4">13. Modification of Terms</h2>
                                <p className="text-white/70 leading-relaxed">
                                    retiringroom.in, operated by JUNES KITCHEN AND BAKERS, reserves the right to update or modify these Terms and Conditions at any time. Continued use of the Website constitutes acceptance of the updated terms.
                                </p>
                            </section>

                            {/* Section 14 */}
                            <section>
                                <h2 className="text-xl font-display font-semibold text-white border-b border-white/10 pb-3 mb-4">14. Governing Law & Jurisdiction</h2>
                                <p className="text-white/70 leading-relaxed">
                                    These Terms and Conditions shall be governed by and construed in accordance with the laws of India.
                                </p>
                                <p className="text-white/70 leading-relaxed">
                                    All disputes shall be subject to the exclusive jurisdiction of courts in India.
                                </p>
                            </section>

                            {/* Privacy Policy Section */}
                            <div className="mt-12 pt-8 border-t border-white/10">
                                <div className="flex items-center gap-3 mb-8">
                                    <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl shadow-lg shadow-emerald-500/30">
                                        <Shield className="w-6 h-6 text-white" />
                                    </div>
                                    <div>
                                        <h2 className="text-2xl font-display font-bold text-white">Privacy Policy</h2>
                                        <p className="text-sm text-white/60">retiringroom.in – Operated by JUNES KITCHEN AND BAKERS</p>
                                    </div>
                                </div>

                                {/* Privacy Section 1 */}
                                <section className="mb-6">
                                    <h3 className="text-lg font-display font-semibold text-white border-b border-white/10 pb-3 mb-4">1. Information We Collect</h3>
                                    <p className="text-white/70 leading-relaxed mb-3">We may collect the following information:</p>
                                    <ul className="list-disc list-inside text-white/70 space-y-2">
                                        <li>Passenger name</li>
                                        <li>Mobile number</li>
                                        <li>ID type and ID number</li>
                                        <li>Booking details (station, date, time, duration)</li>
                                        <li>Payment details (processed securely via third-party gateways)</li>
                                    </ul>
                                </section>

                                {/* Privacy Section 2 */}
                                <section className="mb-6">
                                    <h3 className="text-lg font-display font-semibold text-white border-b border-white/10 pb-3 mb-4">2. Use of Information</h3>
                                    <p className="text-white/70 leading-relaxed mb-3">Collected information is used to:</p>
                                    <ul className="list-disc list-inside text-white/70 space-y-2">
                                        <li>Process and manage bookings</li>
                                        <li>Verify identity at check-in</li>
                                        <li>Communicate booking-related updates</li>
                                        <li>Improve operational efficiency and service quality</li>
                                    </ul>
                                </section>

                                {/* Privacy Section 3 */}
                                <section className="mb-6">
                                    <h3 className="text-lg font-display font-semibold text-white border-b border-white/10 pb-3 mb-4">3. Data Sharing</h3>
                                    <ul className="list-disc list-inside text-white/70 space-y-2">
                                        <li>Personal data may be shared with retiring room station authorities strictly for verification and accommodation purposes.</li>
                                        <li>We do not sell or share personal data for marketing or advertising.</li>
                                    </ul>
                                </section>

                                {/* Privacy Section 4 */}
                                <section className="mb-6">
                                    <h3 className="text-lg font-display font-semibold text-white border-b border-white/10 pb-3 mb-4">4. Data Security</h3>
                                    <p className="text-white/70 leading-relaxed">
                                        Reasonable technical and organizational measures are implemented to protect user data. However, no system can guarantee absolute security.
                                    </p>
                                </section>

                                {/* Privacy Section 5 */}
                                <section className="mb-6">
                                    <h3 className="text-lg font-display font-semibold text-white border-b border-white/10 pb-3 mb-4">5. Cookies</h3>
                                    <p className="text-white/70 leading-relaxed">
                                        Cookies may be used to improve functionality, analyze usage, and enhance user experience.
                                    </p>
                                </section>

                                {/* Privacy Section 6 */}
                                <section className="mb-6">
                                    <h3 className="text-lg font-display font-semibold text-white border-b border-white/10 pb-3 mb-4">6. Changes to Privacy Policy</h3>
                                    <p className="text-white/70 leading-relaxed">
                                        This Privacy Policy may be updated periodically. Continued use of the Website constitutes acceptance of such changes.
                                    </p>
                                </section>

                                {/* Contact Information */}
                                <section>
                                    <h3 className="text-lg font-display font-semibold text-white border-b border-white/10 pb-3 mb-4">7. Contact Information</h3>
                                    <p className="text-white/70 leading-relaxed mb-4">
                                        For booking support, cancellations, or refund-related queries, please contact:
                                    </p>
                                    <div className="bg-gradient-to-br from-white/5 to-white/10 rounded-xl p-6 border border-white/10">
                                        <h4 className="font-display font-semibold text-white mb-4">JUNES KITCHEN AND BAKERS</h4>
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-3 text-white/70">
                                                <Phone className="w-4 h-4 text-railway-400" />
                                                <span>8983044786</span>
                                            </div>
                                            <div className="flex items-center gap-3 text-white/70">
                                                <Mail className="w-4 h-4 text-railway-400" />
                                                <span>info@retiringroom.in</span>
                                            </div>
                                            <div className="flex items-start gap-3 text-white/70">
                                                <MapPin className="w-4 h-4 text-railway-400 mt-1" />
                                                <div className="text-sm">
                                                    <p>Flat/Door/Block No.: H.NO.1/576</p>
                                                    <p>Name of Premises/Building: DHANDE BUILDING</p>
                                                    <p>Village/Town: BHUSAWAL</p>
                                                    <p>Block: S.NO.342A/343</p>
                                                    <p>Road/Street/Lane: V M WARD</p>
                                                    <p>City: BHUSAWAL</p>
                                                    <p>State: MAHARASHTRA</p>
                                                    <p>PIN Code: 425201</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </section>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </PublicLayout>
    );
};

export default TermsAndConditions;
